import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { sendStepCompleteEmail } from '../../../UtilsEmail';

import {
  trackUploadSample,
  trackDownloadShotImage,
  trackClickUploadMore,
} from '../../../analytics';

import { SellingPoint, InfoPoints } from '../../../components/custom';
import { Alert, ProgressBar } from '../../../components/general';
import {
  PageWrapper,
  PageHeader,
  BackBtn,
} from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { EVENT_TYPE_BRAND } from '../../../constants';

import useSampleProjectData from '../../../hooks/useSampleProjectData';

import { getCreationResults } from '../../../services/api/creations';
import { getTemplateFile } from '../../../services/api/template';

import { useProjectStore } from '../../../stores/project';
import { useUserStore } from '../../../stores/user';

import { File, PageError, UploadingFile } from '../../../types';
import {
  isEmpty,
  getFileUrl,
  getThumbnail,
  getShotImage,
} from '../../../utils';

import DoneBtn from '../components/doneBtn';
import FileDropArea from '../components/fileDropArea';
import MobileReminderBox from '../components/mobileReminderBox';
import PageWithSideBar from '../components/pageWithSideBar';
import { UploadStage } from '../components/types';
import UploadMoreBtn from '../components/uploadMoreBtn';
import getHlReelText from './components/getHlReelText';
import HlReelSchema from './components/hlReelSchema';
import HlReelStoryboard from './components/hlReelStoryboard';
import SampleComponent from './components/sampleComponent';

const HighlightReel = () => {
  const { eventId = '' } = useParams();

  const { user } = useUserStore();
  const {
    eventId: eventIdLocal,
    currentProject,
    projectError,
    setEventId,
    setSampleProject,
  } = useProjectStore();

  const [hlReel, setHlReel] = useState<File | null>(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [hasCreations, setHasCreations] = useState(false);

  const [showUpload, setShowUpload] = useState(false); // whether upload area is shown (user clicked "upload more")

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState('');

  const uploadRef = useRef<React.ElementRef<typeof Upload>>(null);

  // sample project data
  const { isSample, sampleProjectData } = useSampleProjectData(eventId);
  const sampleHlReel = sampleProjectData.hlReel || {};

  // assignee data
  const assigneeData = JSON.parse(localStorage.getItem('assigneeData') ?? '{}');
  const isPublic = currentProject?.isAssigneeAccess ?? false;

  // set event id on mount
  useEffect(() => {
    if (isSample) setSampleProject(eventId);
    else if (eventIdLocal !== eventId) setEventId(eventId, true);
  }, [eventId, eventIdLocal, isSample, setSampleProject, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      if (
        currentProject?.isAssigneeAccess &&
        !assigneeData[currentProject?.eventId]?.accessPages.includes('hl-reel')
      ) {
        setPageError(403);
        setIsLoading(false);
        return;
      }

      await fetchHlReelData();
      await fetchCreationsData();

      setIsLoading(false);
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
      return;
    }

    if (currentProject?.eventId)
      if (isSample) {
        if (sampleHlReel.isSelected) setHlReel(sampleHlReel);
        setIsLoading(false);
      } else onMount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject?.eventId, currentProject?.isAssigneeAccess, projectError]);

  const fetchHlReelData = async () => {
    try {
      const file = await getTemplateFile(eventId, isPublic);
      setHlReel(file);
    } catch (err) {
      console.error(`Error fetching the highlight reel: ${err}`);
      setError('Error fetching the highlight reel');
    }
  };

  const fetchCreationsData = async () => {
    try {
      const creations = await getCreationResults(eventId, isPublic);
      setHasCreations(!isEmpty(creations));
    } catch (err) {
      console.error(`Error fetching the creations: ${err}`);
      setError('Error fetching the creations');
    }
  };

  // compute current stage: to-upload | uploading | summary
  const stage: UploadStage = useMemo(() => {
    if (uploadPct > 0 && uploadPct < 100) return 'uploading';
    if (isEmpty(hlReel) || showUpload) return 'to-upload';
    return 'summary';
  }, [hlReel, uploadPct, showUpload]);

  const prevStageRef = useRef(stage);

  const handleDrop = (_file: UploadFile) => {
    setShowUpload(false);
    setUploadPct(1);
  };

  const handleProgress = (file: UploadingFile) => {
    if (file.progress >= 2) setUploadPct(file.progress - 1);
  };

  const handleComplete = async (_file: UploadFile) => {
    await fetchHlReelData();
    setUploadPct(100);
  };

  const handleError = (_file: UploadFile) => {
    setError(
      "It looks like there's an issue with your upload. Please try again.",
    );
  };

  // when upload completes
  useEffect(() => {
    const prevStage = prevStageRef.current;

    if (prevStage === 'uploading' && stage === 'summary') {
      toast.success('The highlight reel is uploaded!', {
        toastId: 'highlight-reel-upload-success',
      });

      localStorage.setItem(
        'uploadData',
        JSON.stringify({
          ...JSON.parse(localStorage.getItem('uploadData') ?? '{}'),
          isHlReelUploaded: true,
        }),
      );
      window.dispatchEvent(new Event('storage'));

      // for sample – track highlight reel upload
      if (isSample) trackUploadSample('hl_reel');
      // send email to the user that the upload is complete
      else if (!isPublic && user && currentProject)
        sendStepCompleteEmail(user, currentProject, 'Highlight reel upload');
    }

    prevStageRef.current = stage;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const handleUploadMore = (
    e: React.MouseEvent<HTMLElement>,
    position: string,
  ) => {
    setShowUpload(true);
    setTimeout(() => uploadRef.current?.upload?.uploader?.onClick(e));
    trackClickUploadMore('hl_reel', position, isSample);
  };

  const allowUploadMore = !isSample && !hasCreations;

  const { subheading, infoPoints } = getHlReelText({
    stage,
    orientation: currentProject?.videoMode,
    hasCreations,
    isPublic,
    isSample,
  });

  return (
    <PageWrapper
      type={isPublic ? 'header-only' : 'project'}
      title={`Highlight Reel – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
    >
      <BackBtn label="Video Builder" />

      <PageWithSideBar
        mainContent={
          <>
            <PageHeader heading="Highlight Reel" subheading={subheading} />

            {/* to upload view */}
            {stage === 'to-upload' && (
              <React.Fragment>
                {currentProject?.type === EVENT_TYPE_BRAND && <HlReelSchema />}

                <SampleComponent
                  setHlReel={setHlReel}
                  setUploadPct={setUploadPct}
                />

                <FileDropArea
                  type="hl-reel"
                  uploadRef={uploadRef}
                  onDrop={handleDrop}
                  onProgress={handleProgress}
                  onComplete={handleComplete}
                  onError={handleError}
                  isSample={isSample}
                />
              </React.Fragment>
            )}

            {/* uploading view */}
            {stage === 'uploading' && (
              <div className="flex flex-col gap-4 text-center">
                <ProgressBar progress={uploadPct} />

                <div className="font-bold uppercase">Uploading</div>
              </div>
            )}

            {/* summary view */}
            {stage === 'summary' && hlReel && (
              <React.Fragment>
                <video
                  className="w-full sm:w-[calc(var(--page-width)_*_0.55)] aspect-video bg-black rounded-md object-contain"
                  src={
                    hlReel.url ??
                    getFileUrl(hlReel.elementId, hlReel.name, undefined, 0.5)
                  }
                  poster={
                    hlReel.thumbnailUrl ??
                    (hlReel.frameId ? getThumbnail(hlReel.frameId) : undefined)
                  }
                  controls
                />

                <div className="flex flex-col sm:flex-row gap-4">
                  <DoneBtn addClass={allowUploadMore ? 'sm:w-1/2' : ''} />

                  {allowUploadMore && (
                    <UploadMoreBtn
                      onClick={(e: React.MouseEvent<HTMLElement>) =>
                        handleUploadMore(e, 'Bottom button')
                      }
                    >
                      Re-Upload
                    </UploadMoreBtn>
                  )}
                </div>
              </React.Fragment>
            )}
          </>
        }
        sideContent={
          <>
            {hasCreations && (
              <Alert generatedAlertText="You cannot upload a new highlight reel." />
            )}

            {!isSample && stage !== 'summary' && (
              <SellingPoint text="Each wildcard needs a matching clip for every person. If someone doesn’t have enough clips, they may not get a personalised video." />
            )}

            <InfoPoints points={infoPoints} isInfo={stage === 'summary'} />

            {!isSample && (
              <Link to={getShotImage('zip')} onClick={trackDownloadShotImage}>
                Download SHOT image
              </Link>
            )}

            {!isSample && !isPublic && <MobileReminderBox />}
          </>
        }
        sampleTipType={isSample ? stage : undefined}
      />

      {stage === 'to-upload' && <HlReelStoryboard className="mt-6 lg:mt-10" />}

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
      />
    </PageWrapper>
  );
};

export default HighlightReel;
